import { useEffect } from 'react';

const SITE_NAME = 'The Nut Shop';
const DEFAULT_DESCRIPTION = 'The Nut Shop - Premium quality nuts, dried fruits, and healthy snacks delivered to your door.';

interface SEOOptions {
  title?: string;
  description?: string;
  ogType?: string;
  ogImage?: string;
  noIndex?: boolean;
}

/**
 * Custom hook for managing page SEO (title, meta description, OG tags)
 */
export function useSEO({ title, description, ogType = 'website', ogImage, noIndex = false }: SEOOptions = {}) {
  useEffect(() => {
    // Title
    document.title = title ? `${title} - ${SITE_NAME}` : SITE_NAME;

    // Meta description
    const desc = description || DEFAULT_DESCRIPTION;
    setMeta('description', desc);

    // Open Graph
    setMeta('og:title', document.title, true);
    setMeta('og:description', desc, true);
    setMeta('og:type', ogType, true);
    if (ogImage) setMeta('og:image', ogImage, true);

    // Twitter
    setMeta('twitter:title', document.title);
    setMeta('twitter:description', desc);

    // Robots
    if (noIndex) {
      setMeta('robots', 'noindex, nofollow');
    } else {
      removeMeta('robots');
    }

    return () => {
      document.title = SITE_NAME;
      setMeta('description', DEFAULT_DESCRIPTION);
      setMeta('og:title', SITE_NAME, true);
      setMeta('og:description', DEFAULT_DESCRIPTION, true);
      removeMeta('robots');
    };
  }, [title, description, ogType, ogImage, noIndex]);
}

function setMeta(name: string, content: string, isProperty = false) {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
}

function removeMeta(name: string) {
  const el = document.querySelector(`meta[name="${name}"]`);
  if (el) el.remove();
}

export default useSEO;
